/**
 * A comp report's rows by id through POST /api/comps/report-entries
 * (CLEAN-UP item 3, finding CR-F1): the properties, sale transactions and
 * lease transactions a report's members (and its subject property) point at —
 * exactly those ids, with the columns the generated list routes answer.
 *
 * Replaces the three `limit: 1000` reference lists the report pages and the
 * append-comps modal joined against: a member past row 1,000 silently dropped
 * out of the report. One request (per 500 ids of a kind) instead of three
 * 1,000-row lists; no request when there are no ids.
 *
 * Keys: `['comp-report-entries', ids]`. The rows carry three tables' data, so
 * no table key reaches them: main.tsx's MutationCache invalidates
 * `compReportEntriesKeys.all` after every successful mutation (the RP-3 /
 * RP-4 / RP-5 pattern for composed reads).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { httpClient } from '../../http-client';
import type { PropertiesRow } from '../../../types/generated_schemas/generated-properties-schema';
import type { SaleTransactionsRow } from '../../../types/generated_schemas/generated-sale_transactions-schema';
import type { LeaseTransactionsRow } from '../../../types/generated_schemas/generated-lease_transactions-schema';

export type CompReportEntryIds = { propertyIds?: string[]; saleIds?: string[]; leaseIds?: string[] };
type Ids = Required<CompReportEntryIds>;
/** A lease row also says whether it is managed-only (Property management PM-2: marked managed, no Colliers broker on it). */
export type ReportLeaseRow = LeaseTransactionsRow & { managed_only?: boolean };
type Rows = {
  properties: PropertiesRow[];
  saleTransactions: SaleTransactionsRow[];
  leaseTransactions: ReportLeaseRow[];
  /** Ids asked for that match no row: hard-deleted (D2-S S8). A transaction the viewer may not see is never here. */
  missing?: CompReportEntryIds;
};

/** The server's per-set cap (MAX_REPORT_ENTRY_IDS). */
const MAX_IDS = 500;

export const compReportEntriesKeys = {
  all: ['comp-report-entries'] as const,
  ids: (ids: Ids) => ['comp-report-entries', ids] as const,
};

const distinctSorted = (ids: ReadonlyArray<string | null | undefined> | undefined) =>
  [...new Set((ids ?? []).filter((id): id is string => Boolean(id)))].sort();

/** The id sets as request bodies of at most MAX_IDS ids per set. */
export function reportEntryBodies(ids: Ids): Ids[] {
  const windows = Math.max(1, ...Object.values(ids).map((v) => Math.ceil(v.length / MAX_IDS)));
  return Array.from({ length: windows }, (_, i) => ({
    propertyIds: ids.propertyIds.slice(i * MAX_IDS, (i + 1) * MAX_IDS),
    saleIds: ids.saleIds.slice(i * MAX_IDS, (i + 1) * MAX_IDS),
    leaseIds: ids.leaseIds.slice(i * MAX_IDS, (i + 1) * MAX_IDS),
  }));
}

async function fetchReportEntries(ids: Ids): Promise<Rows> {
  const pages = await Promise.all(
    reportEntryBodies(ids).map(async (body) => (await httpClient.post<{ data: Rows }>('/comps/report-entries', body)).data),
  );
  return {
    properties: pages.flatMap((p) => p.properties),
    saleTransactions: pages.flatMap((p) => p.saleTransactions),
    leaseTransactions: pages.flatMap((p) => p.leaseTransactions),
    missing: {
      propertyIds: pages.flatMap((p) => p.missing?.propertyIds ?? []),
      saleIds: pages.flatMap((p) => p.missing?.saleIds ?? []),
      leaseIds: pages.flatMap((p) => p.missing?.leaseIds ?? []),
    },
  };
}

/**
 * The rows of `ids`, as id → row maps. An id the server did not return (deleted, or a
 * transaction the viewer may not see) is absent; `deleted` holds the ones that are gone.
 */
export function useCompReportEntries(ids: CompReportEntryIds) {
  const propertyKey = distinctSorted(ids.propertyIds).join(',');
  const saleKey = distinctSorted(ids.saleIds).join(',');
  const leaseKey = distinctSorted(ids.leaseIds).join(',');
  const normalized: Ids = useMemo(() => {
    const split = (key: string) => (key ? key.split(',') : []);
    return { propertyIds: split(propertyKey), saleIds: split(saleKey), leaseIds: split(leaseKey) };
  }, [propertyKey, saleKey, leaseKey]);

  const q = useQuery<Rows>({
    queryKey: compReportEntriesKeys.ids(normalized),
    queryFn: () => fetchReportEntries(normalized),
    enabled: Boolean(propertyKey || saleKey || leaseKey),
  });

  const propertyById = useMemo(() => new Map((q.data?.properties ?? []).map((p) => [p.id, p])), [q.data]);
  const saleById = useMemo(() => new Map((q.data?.saleTransactions ?? []).map((s) => [s.id, s])), [q.data]);
  const leaseById = useMemo(() => new Map((q.data?.leaseTransactions ?? []).map((l) => [l.id, l])), [q.data]);
  const deleted = useMemo(() => {
    const m = q.data?.missing;
    return new Set([...(m?.propertyIds ?? []), ...(m?.saleIds ?? []), ...(m?.leaseIds ?? [])]);
  }, [q.data]);
  return { propertyById, saleById, leaseById, deleted, isLoading: q.isLoading };
}
